import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ExpandableRow from '@/components/ui/ExpandableRow';
import { Log } from '@/lib/api';
import { formatDateTimeForDisplay } from '@/lib/date';
import { getLogLevelColor } from '@/lib/utils/formatters';

interface ExpandableLogRowProps {
  log: Log;
}

/**
 * Expandable log row component
 * Displays a log row and shows detailed information when clicked
 */
export const ExpandableLogRow: React.FC<ExpandableLogRowProps> = ({ log }) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleToggle = () => setIsOpen(!isOpen);

  // Main cells for the log row - content only, no TableCell wrappers
  // ExpandableRow will wrap these in TableCell components
  const logCells = [
    // Timestamp cell content
    <Box key="timestamp" sx={{ width: '180px' }}>
      {formatDateTimeForDisplay(log.timestamp)}
    </Box>,
    // Level cell content
    <Box key="level">
      <Chip label={log.level} color={getLogLevelColor(log.level)} size="small" />
    </Box>,
    // Message cell content
    <Box key="message" sx={{
      maxWidth: '800px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}>
      {log.message}
    </Box>,
  ];

  const { t } = useTranslation('logs');

  // Content to display when expanded
  const expandedContent = (
    <Table size="small" aria-label={t('table.details')}>
      <TableBody>
        <TableRow>
          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '150px', whiteSpace: 'nowrap' }}>
            {t('table.timestamp')}
          </TableCell>
          <TableCell>{formatDateTimeForDisplay(log.timestamp)}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '150px', whiteSpace: 'nowrap' }}>
            {t('table.level')}
          </TableCell>
          <TableCell>
            <Chip label={log.level} color={getLogLevelColor(log.level)} size="small" />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '150px', whiteSpace: 'nowrap' }}>
            {t('table.file')}
          </TableCell>
          <TableCell>
            {log.file ? (log.line ? `${log.file}:L${log.line}` : log.file) : '-'}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '150px', whiteSpace: 'nowrap' }}>
            {t('table.message')}
          </TableCell>
          <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {log.message}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );

  return (
    <ExpandableRow
      id={`log-${log.id}`}
      cells={logCells}
      expandedContent={expandedContent}
      colSpan={4}
      open={isOpen}
      onToggle={handleToggle}
    />
  );
};

export default ExpandableLogRow;
